import { useState } from "react";
import Avatar1 from "../../../assets/images/png/avatars/1.webp";
import Avatar2 from "../../../assets/images/png/avatars/2.webp";
import Avatar3 from "../../../assets/images/png/avatars/3.webp";
import VideoModal from "../../../components/modals/VideoModal";
import HeaderNavigation from "../../../components/layout/HeaderNavigation";
import { ArrowUpRight } from "../../../components/svgs";

const HERO_AVATARS = [Avatar1, Avatar2, Avatar3];

const SECTION_TABS = [
  { id: "description", label: "Description" },
  { id: "curriculum", label: "Curriculum" },
  { id: "portfolio", label: "Portfolio" },
  { id: "certificate", label: "Certification" },
  { id: "instructor", label: "Instructor" },
  { id: "coaches", label: "Coaches" },
  { id: "pricing", label: "Pricing" },
];

const getYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:embed\/|v=|youtu\.be\/)([a-zA-Z0-9_-]{6,})/);
  return match ? match[1] : null;
};

const useCourseLinks = (courses, selectedCourseData, selectedLocation) => {
  const course = courses?.find((a) => a?.title === selectedCourseData?.title);
  const enrolLink = course?.enrolLink || "/";
  const isAfrica = selectedLocation?.name === "Africa" || !selectedLocation;
  const whatsappUrl = isAfrica
    ? "https://wa.me/2348035152982"
    : selectedCourseData?.chatWithUsUrl || "https://wa.me/12028217864";
  return { enrolLink, whatsappUrl };
};

export const CourseTopBar = ({ courses, selectedCourseData }) => {
  const { enrolLink } = useCourseLinks(courses, selectedCourseData);

  return (
    <header>
      <HeaderNavigation />

      <div className="bg-primary font-poppins text-white flex items-center justify-center gap-3 md:gap-6 py-2.5 px-4 text-base text-center">
        <span className="hidden sm:inline-flex font-poppins bg-secondary text-primary rounded-full px-2.5 py-1 text-base font-extrabold tracking-wide whitespace-nowrap">
          {(selectedCourseData?.duration || "16 WEEKS").toUpperCase()}
        </span>
        <p className="m-0 hidden font-poppins sm:block">
          Go from problem discovery to a portfolio-ready product with live
          weekend classes.
        </p>
        <a
          href={enrolLink}
          target="_blank"
          rel="noopener noreferrer"
          className="font-bold underline font-poppins underline-offset-4 whitespace-nowrap"
        >
          Enrol now <ArrowUpRight className="w-3.5 h-3.5 inline-block" />
        </a>
      </div>
    </header>
  );
};

const CohortCard = ({
  selectedCourseData,
  cohortData,
  cohortLoading,
  enrolLink,
}) => (
  <div className="border border-[#DFE6F7] rounded-[26px] bg-white shadow-course-card overflow-hidden max-w-[780px]">
    <div className="bg-[#F0F5FF] font-poppins text-primary flex flex-wrap justify-between gap-2 px-5 py-3 text-base font-bold">
      <span>Program snapshot</span>
      <span>Live online · Weekends only</span>
    </div>

    <div className="flex flex-col md:flex-row md:items-stretch gap-4 p-5">
      <div className="flex-1 flex flex-col justify-center">
        <small className="text-primary font-poppins tracking-wider text-base font-extrabold block mb-1">
          PROGRAM DURATION
        </small>
        <strong className="text-primary font-poppins text-2xl md:text-3xl leading-tight block">
          {selectedCourseData?.duration}
        </strong>
        {selectedCourseData?.durationDetail && (
          <span className="text-[#565656] font-poppins text-sm block mt-1">
            {selectedCourseData.durationDetail}
          </span>
        )}
      </div>

      <div className="flex-1 bg-[#FFFBF2] rounded-2xl px-4 py-3.5 flex flex-col justify-center">
        <small className="text-primary font-poppins tracking-wider text-base font-extrabold block mb-1">
          NEXT COHORT
        </small>
        <span className="text-primary font-poppins text-lg md:text-xl font-extrabold block">
          {cohortLoading ? "Loading..." : cohortData?.date || "Not specified"}
        </span>
        <span className="text-[#565656] font-poppins text-sm block mt-1">
          Secure your place in the next class
        </span>
      </div>

      <div className="flex md:items-center md:flex-none">
        <a
          href={enrolLink}
          target="_blank"
          rel="noopener noreferrer"
          className="custom-btn font-poppins flex items-center justify-center gap-3 w-full md:w-auto bg-primary text-white font-bold text-base rounded-xl px-8 py-4 focus:outline-none whitespace-nowrap"
        >
          Enrol now{" "}
          <span aria-hidden="true">
            <ArrowUpRight className="w-3.5 h-3.5 inline-block" />
          </span>
        </a>
      </div>
    </div>

    <div className="flex items-center gap-3 px-5 pb-5">
      <span
        className="flex-1 h-1 rounded-full"
        style={{
          background: "linear-gradient(90deg, #FBAF1B 72%, #DFE6F7 72%)",
        }}
      />
      <p className="text-[#565656] font-poppins text-base m-0 whitespace-nowrap">
        Expert-led classes · Real-world projects
      </p>
    </div>
  </div>
);

const CourseAdvisorCard = ({ selectedCourseData, whatsappUrl, enrolLink }) => {
  const videoId = getYouTubeId(selectedCourseData?.videoTestimonial);
  const [showVideo, setShowVideo] = useState(false);

  return (
    <div className="border border-[#DFE6F7] rounded-[26px] bg-white shadow-course-card p-4">
      {videoId && (
        <button
          type="button"
          onClick={() => setShowVideo(true)}
          className="relative flex w-full bg-[#F0F5FF] rounded-2xl overflow-hidden h-44 focus:outline-none"
        >
          <img
            src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
            alt={`${selectedCourseData?.title} video preview`}
            className="w-full h-full object-cover"
          />
          <span className="absolute inset-0 m-auto w-16 h-16 rounded-full bg-white/95 text-primary grid place-items-center text-xl shadow-course-card">
            ▶
          </span>
        </button>
      )}

      {showVideo && (
        <VideoModal
          show={showVideo}
          handleModalClose={() => setShowVideo(false)}
          link={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
        />
      )}

      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`${
          videoId ? "mt-4" : ""
        } flex items-center justify-center font-poppins gap-3 bg-primary text-white font-bold text-base rounded-xl py-4 focus:outline-none`}
      >
        Speak with An Advisor{" "}
        <span aria-hidden="true">
          <ArrowUpRight className="w-3.5 h-3.5 inline-block" />
        </span>
      </a>
      <a
        href={enrolLink}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center font-poppins mt-2 justify-center gap-2 border border-[#DFE6F7] text-primary font-bold text-base rounded-xl py-4 focus:outline-none"
      >
        Start your applications{" "}
        <span aria-hidden="true">
          <ArrowUpRight className="w-3.5 h-3.5 inline-block" />
        </span>
      </a>

      <CourseDetailsCard selectedCourseData={selectedCourseData} />
    </div>
  );
};

const CourseDetailsCard = ({ selectedCourseData }) => (
  <div className="h-full border-[#DFE6F7] font-poppins pt-3 bg-white shadow-course-card">
    <dl className="">
      {[
        ["Duration", selectedCourseData?.duration],
        ["Format", "Live online"],
        ["Schedule", "Weekends"],
        ["Learning model", "50 / 50"],
      ].map(([label, value]) => (
        <div
          key={label}
          className="flex items-center justify-between gap-4 py-1.5 text-[11px]"
        >
          <dt className="text-[#565656] font-poppins text-base">{label}</dt>
          <dd className="text-[#1E2027] font-poppins text-base font-bold m-0">
            {value}
          </dd>
        </div>
      ))}
    </dl>

    {selectedCourseData?.downloadBrochureUrl && (
      <a
        href={selectedCourseData.downloadBrochureUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 flex items-center font-poppins justify-center gap-2 bg-[#FFFBF2] text-primary font-bold text-base rounded-xl py-3 focus:outline-none"
      >
        ↓ Download brochure
      </a>
    )}
  </div>
);

export const CourseHeroContent = ({
  courses,
  selectedCourseData,
  selectedLocation,
  cohortData,
  cohortLoading,
}) => {
  const { enrolLink, whatsappUrl } = useCourseLinks(
    courses,
    selectedCourseData,
    selectedLocation
  );

  return (
    <div className="pt-20 lg:pt-28">
      <p className="text-primary tracking-[0.19em] font-poppins uppercase text-base font-extrabold flex items-center gap-2 mb-8">
        <span className="inline-block w-6 h-[3px] bg-secondary rounded-full" />
        {selectedCourseData?.tag || "Product School"} · Cohort Program
      </p>

      <h1 className="text-primary font-black font-poppins leading-[1.07] tracking-[-0.055em] text-3xl md:text-6xl lg:text-[64px] mb-10 max-w-[790px]">
        {selectedCourseData?.title}
      </h1>

      <p className="text-[#565656] text-base font-poppins lg:text-xl leading-[1.75] max-w-[760px] mb-10">
        {selectedCourseData?.heroSubtitle || selectedCourseData?.description}
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-12">
        <span className="flex">
          {HERO_AVATARS.map((avatar, i) => (
            <img
              key={avatar}
              src={avatar}
              alt=""
              className={`w-7 h-7 rounded-full border-2 border-white object-cover ${
                i > 0 ? "-ml-2" : ""
              }`}
            />
          ))}
        </span>
        <strong className="text-[15px] font-poppins">
          {selectedCourseData?.traineeCountLabel ||
            "30,000+ professionals trained"}
        </strong>
        <span className="text-secondary tracking-wider">★★★★★</span>
        <small className="text-[#565656] font-poppins text-[15px]">
          learner-led community
        </small>
      </div>

      <CohortCard
        selectedCourseData={selectedCourseData}
        cohortData={cohortData}
        cohortLoading={cohortLoading}
        enrolLink={enrolLink}
      />

      <div className="lg:hidden mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
        <CourseAdvisorCard
          selectedCourseData={selectedCourseData}
          whatsappUrl={whatsappUrl}
          enrolLink={enrolLink}
        />
        {/* <CourseDetailsCard selectedCourseData={selectedCourseData} /> */}
      </div>
    </div>
  );
};

export const CourseSectionTabs = () => (
  <nav
    aria-label="Course sections"
    className="sticky z-20 bg-white/95 backdrop-blur border-b border-[#DFE6F7] overflow-x-auto no-scrollbar mt-10 lg:mt-14"
    style={{ top: "var(--header-height, 0px)" }}
  >
    <div className="flex gap-6 py-4">
      {SECTION_TABS.map((tab, i) => (
        <a
          key={tab.id}
          href={`#${tab.id}`}
          className={`whitespace-nowrap flex-none text-[15px] font-semibold ${
            i === 0 ? "text-primary font-bold" : "text-[#565656]"
          }`}
        >
          {tab.label}
        </a>
      ))}
    </div>
  </nav>
);

export const CourseSidebar = ({
  courses,
  selectedCourseData,
  selectedLocation,
}) => {
  const { enrolLink, whatsappUrl } = useCourseLinks(
    courses,
    selectedCourseData,
    selectedLocation
  );

  return (
    <div className="hidden lg:block space-y-4">
      <CourseAdvisorCard
        selectedCourseData={selectedCourseData}
        whatsappUrl={whatsappUrl}
        enrolLink={enrolLink}
      />
    </div>
  );
};

export const CourseMobileEnrolBar = ({ courses, selectedCourseData }) => {
  const { enrolLink } = useCourseLinks(courses, selectedCourseData);

  return (
    <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-[#DFE6F7] flex items-center justify-between gap-4 px-4 py-3">
      <span className="flex flex-col">
        <strong className="text-sm font-poppins text-[#1E2027]">
          {selectedCourseData?.duration}
        </strong>
        <small className="text-[#565656] font-poppins text-[9px]">
          Live online · Weekends
        </small>
      </span>
      <a
        href={enrolLink}
        target="_blank"
        rel="noopener noreferrer"
        className="bg-primary text-white font-poppins text-xs font-bold rounded-xl px-5 py-3 focus:outline-none whitespace-nowrap"
      >
        Enrol now
      </a>
    </div>
  );
};
